import React from 'react';
import { Box } from 'src/components/Box';
import { Fab } from 'src/components/Fab';
import { Typography } from 'src/components/Typography';
import { useTheme } from 'src/components/muiTheme/utils';

import { useSidebarDialog } from './SidebarDialogContext';
import { SidebarDialogFabProps } from './SidebarDialogTypes';

export const SidebarDialogFab: React.FC<SidebarDialogFabProps> = ({
  text,
  startAdornment,
  endAdornment,
  active = false,
  disabled = false,
  onClick,
  sx,
}) => {
  const theme = useTheme();
  const { isMobile } = useSidebarDialog();

  const fabSxArray = [
    (theme) => ({
      pl: 2,
      height: '56px',
      borderRadius: '28px',
      backdropFilter: 'blur(5px)',
      textTransform: 'none',
      color: 'white',
      '&.Mui-disabled': {
        opacity: 1,
        color: theme.palette.neutral[50],
        backgroundColor: `rgba(${theme.vars.palette.neutral['500Channel']} / 0.9)`,
        boxShadow: '0px 12px 10px 0px rgba(102, 112, 133, 0.13)',
      },
    }),
    // Enhanced styling when active
    active && {
      backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.9)`,
      boxShadow: `0px 12px 10px 0px rgba(
        ${theme.vars.palette.primary.mainChannel} / 0.4
      )`,
      ...theme.applyStyles('dark', {
        boxShadow: `0px 12px 10px 0px rgba(
          ${theme.vars.palette.primary.mainChannel} / 0.2
        )`,
      }),
    },
    ...(Array.isArray(sx) ? sx : [sx]),
  ];

  const fabContent = (
    <>
      {startAdornment && <Box sx={{ mr: 1 }}>{startAdornment}</Box>}
      <Typography
        variant="body1"
        sx={{ mx: startAdornment || endAdornment ? 1 : 0 }}
      >
        {text}
      </Typography>
      {endAdornment && <Box sx={{ ml: 1 }}>{endAdornment}</Box>}
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        bottom: '0px',
        width: '100%',
        position: isMobile ? 'fixed' : 'absolute',
        mb: 4,
      }}
    >
      <Fab
        size="large"
        variant="extended"
        color={active ? 'primary' : 'secondary'}
        disabled={disabled}
        onClick={onClick}
        sx={fabSxArray}
      >
        {fabContent}
      </Fab>
    </Box>
  );
};

// Add displayName for auto-detection in SidebarDialog
SidebarDialogFab.displayName = 'SidebarDialogFab';
